/* Cart extras use the same private group ID and public properties as product pages. */
if (!customElements.get('goose-cart-extras')) {
  const cardSizes = '(max-width: 520px) calc(50vw - 45px), 190px';
  // Keep the same responsive sources warm across cart section replacements.
  const imageCache = new Map();
  function warmImage(source, sizes = cardSizes, priority = 'low') {
    if (!source?.image) return;
    const key = `${source.srcset || source.image}|${sizes}`;
    const cached = imageCache.get(key);
    const ranks = { low: 0, auto: 1, high: 2 };
    if (cached) {
      if (ranks[priority] > ranks[cached.fetchPriority || 'auto']) cached.fetchPriority = priority;
      return cached;
    }
    const image = new Image();
    image.loading = 'eager'; image.decoding = 'async'; image.fetchPriority = priority;
    image.sizes = sizes;
    if (source.srcset) image.srcset = source.srcset;
    imageCache.set(key, image);
    image.addEventListener('error', () => imageCache.delete(key), { once: true });
    image.src = source.image;
    return image;
  }
  customElements.define('goose-cart-extras', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.config = JSON.parse(this.querySelector('[data-extra-data]').textContent);
      // Older cached section HTML still supplies filenames for these four photos.
      this.positionPhotos = this.config.positionPhotos || this.config.positionImages.map(filename => ({
        image: `https://cdn.shopify.com/s/files/1/0829/5520/6930/files/${filename}?width=320`, srcset: ''
      }));
      this.text = this.config.text;
      this.hatSelect = this.querySelector('[data-extra-hat]');
      this.quantity = this.querySelector('[data-extra-quantity]');
      this.rootURL = window.Shopify?.routes?.root || '/';
      this.addEventListener('change', event => this.onChange(event));
      this.addEventListener('input', event => {
        if (event.target.matches('[data-extra-initials]')) event.target.value = event.target.value.replace(/[^a-z0-9]/gi, '').slice(0, 3).toUpperCase();
      });
      this.addEventListener('submit', event => {
        if (!event.target.matches('[data-extra-form]')) return;
        event.preventDefault();
        this.save(event.target);
      });
      // Details summaries remain keyboard accessible; keep only one panel expanded.
      this.querySelectorAll('details').forEach(details => details.addEventListener('toggle', () => {
        if (!details.open) return;
        this.querySelectorAll('details').forEach(other => { if (other !== details) other.open = false; });
        details.querySelectorAll('img').forEach((image, index) => {
          image.loading = 'eager';
          image.fetchPriority = index < 2 ? 'high' : 'auto';
          warmImage({ image: image.src, srcset: image.getAttribute('srcset') }, image.sizes || cardSizes, image.fetchPriority);
        });
      }));
      if (this.hatSelect.options.length === 2) this.hatSelect.selectedIndex = 1;
      this.selectHat();
      this.prefetchTimer = setTimeout(() => { if (this.isConnected) this.primeImages(); }, 250);
    }

    disconnectedCallback() { clearTimeout(this.prefetchTimer); }
    primeImages() {
      [...this.config.bands, ...this.config.feathers].forEach(product => {
        warmImage(product);
        product.variants.forEach(variant => warmImage(variant));
      });
      this.positionPhotos.forEach(source => warmImage(source, '160px'));
    }

    get hat() { return this.config.items.find(item => item.key === this.hatSelect.value); }
    get group() { return this.hat?.properties?._Personalizacion_ID; }
    extra(kind) {
      return this.config.items.find(item => this.group && item.properties?._Personalizacion_ID === this.group &&
        (kind === 'engraving' ? Number(item.variant_id) === Number(this.config.service.id) :
          item.properties?._Complemento === (kind === 'band' ? 'Toquilla adicional' : 'Pluma adicional')));
    }
    money(cents) {
      const lang = document.documentElement.lang, currency = this.dataset.currency;
      const locale = lang === 'es' ? 'es-MX' : lang;
      return `${new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).format(cents / 100)} ${currency}`;
    }
    status(message, failed = false) {
      const node = this.querySelector('[data-extra-status]');
      node.textContent = message;
      node.toggleAttribute('data-error', failed);
      return node;
    }
    options(select, entries) {
      select.replaceChildren(new Option(this.text.choose, ''));
      entries.forEach(([value, label]) => select.add(new Option(label, value)));
      if (entries.length === 1) select.selectedIndex = 1;
    }
    renderCards(container, entries, name, attribute) {
      const fragment = document.createDocumentFragment();
      entries.forEach(entry => {
        const label = document.createElement('label');
        label.className = 'goose-cart-extras__card';
        const radio = document.createElement('input');
        radio.type = 'radio'; radio.name = name; radio.value = entry.value; radio.required = true;
        radio.setAttribute(attribute, '');
        if (entries.length === 1) radio.checked = true;
        label.append(radio);
        if (entry.image) {
          const image = document.createElement('img');
          image.width = 160; image.height = 160; image.alt = '';
          image.loading = 'eager';
          image.fetchPriority = container.closest('details').open ? 'auto' : 'low';
          image.decoding = 'async';
          image.sizes = cardSizes;
          if (entry.srcset) image.srcset = entry.srcset;
          image.src = entry.image;
          label.append(image);
        }
        const title = document.createElement('span');
        title.className = 'goose-cart-extras__card-name';
        title.textContent = entry.title.replace(/\s+/g, ' ').trim();
        const price = document.createElement('span');
        price.className = 'goose-cart-extras__card-price'; price.textContent = entry.price;
        label.append(title, price);
        fragment.append(label);
      });
      container.replaceChildren(fragment);
    }
    compatible(variant) {
      const size = window.GooseOptions?.sizeRange(this.hatSelect.selectedOptions[0]?.dataset.size)?.[0];
      return !!window.GooseOptions?.compatible(variant.size, size);
    }
    selectHat() {
      const hat = this.hat;
      this.querySelector('[data-extra-controls]').disabled = !hat;
      const wrap = this.querySelector('[data-extra-quantity-wrap]');
      wrap.hidden = !hat || hat.quantity < 2 || !!this.group;
      this.quantity.replaceChildren();
      if (hat) for (let n = 1; n <= hat.quantity; n++) this.quantity.add(new Option(String(n), String(n)));
      if (this.group && hat) this.quantity.value = String(hat.quantity);
      const target = this.hatSelect.selectedOptions[0]?.textContent.replace(/\s+/g, ' ').trim();
      const help = this.group && hat?.quantity > 1 ? this.text.group_help : this.text.quantity_help;
      this.querySelector('[data-extra-target]').textContent = !hat ? this.text.hat_help : `${target}. ${help}`;
      this.querySelectorAll('[data-extra-form]').forEach(form => {
        form.reset();
        const kind = form.dataset.extraForm, existing = this.extra(kind), note = form.querySelector('[data-extra-existing]');
        const engravingAllowed = this.hatSelect.selectedOptions[0]?.dataset.engraving === 'true' && this.config.service.available;
        note.hidden = !existing && (kind !== 'engraving' || engravingAllowed || !hat);
        note.textContent = existing ? (kind === 'engraving' ? this.text.existing : this.text.linked_remove) : this.text.service_unavailable;
        const button = form.querySelector('[data-extra-submit]');
        button.textContent = kind === 'engraving' && existing ? this.text.save_engraving : this.text.add;
        if (kind === 'engraving') {
          button.disabled = !hat || !engravingAllowed;
          form.querySelector('[data-extra-initials]').value = hat?.properties?.Grabado || hat?.properties?.Engraving || '';
          const position = hat?.properties?.['Posición del grabado'] || hat?.properties?.['Engraving position'];
          const radio = [...form.querySelectorAll('[data-extra-position]')].find(input => input.value === position);
          if (radio) radio.checked = true;
          this.updatePosition(form);
        } else this.updateProducts(form);
      });
      this.status('');
    }
    updateProducts(form) {
      const kind = form.dataset.extraForm;
      const products = kind === 'band' ? this.config.bands : this.config.feathers;
      const category = form.querySelector('[data-extra-category]')?.value;
      form.availableProducts = products.filter(product => (kind !== 'band' || category === 'all' || product.category === category) &&
        product.variants.some(variant => kind !== 'band' || this.compatible(variant)));
      const note = form.querySelector('[data-extra-existing]');
      if (!this.extra(kind)) {
        const empty = this.hat && (kind !== 'band' || category) && !form.availableProducts.length;
        note.hidden = !empty;
        note.textContent = empty ? this.text.unavailable : '';
      }
      const entries = form.availableProducts.map((product, index) => {
        const variants = product.variants.filter(variant => kind !== 'band' || this.compatible(variant));
        const prices = variants.map(variant => variant.price);
        return { value: String(index), title: product.title, image: product.image || variants[0].image,
          srcset: product.srcset || variants[0].srcset,
          price: `${new Set(prices).size > 1 ? this.text.from + ' ' : ''}${this.money(Math.min(...prices))}` };
      });
      this.renderCards(form.querySelector('[data-extra-product-cards]'), entries, `product-${kind}`, 'data-extra-product');
      this.updateVariants(form);
    }
    updateVariants(form) {
      const value = form.querySelector('[data-extra-product]:checked')?.value;
      form.selectedProduct = value === undefined ? null : form.availableProducts[Number(value)];
      form.availableVariants = (form.selectedProduct?.variants || []).filter(variant => form.dataset.extraForm !== 'band' || this.compatible(variant));
      form.querySelector('[data-extra-variants]').hidden = !form.availableVariants.length;
      this.renderCards(form.querySelector('[data-extra-variant-cards]'), form.availableVariants.map(variant => ({
        value: String(variant.id), title: variant.title, image: variant.image, srcset: variant.srcset, price: this.money(variant.price)
      })), `variant-${form.dataset.extraForm}`, 'data-extra-variant');
      this.updatePreview(form);
    }
    updatePreview(form) {
      const id = form.querySelector('[data-extra-variant]:checked')?.value;
      form.selectedVariant = form.availableVariants.find(variant => String(variant.id) === id);
      const variant = form.selectedVariant;
      form.querySelector('[data-extra-preview]').hidden = !variant;
      if (variant) {
        form.querySelector('[data-extra-price]').textContent = `${this.money(variant.price)} ${this.text.per_hat}`;
      }
      const button = form.querySelector('[data-extra-submit]');
      button.disabled = !this.hat || !variant || !!this.extra(form.dataset.extraForm);
      button.textContent = variant ? `${this.text.add} · ${this.money(variant.price)}` : this.text.add;
    }
    updatePosition(form) {
      const radios = [...form.querySelectorAll('[data-extra-position]')], index = radios.findIndex(input => input.checked);
      const image = form.querySelector('[data-extra-position-photo]');
      image.hidden = index < 0;
      if (index >= 0) {
        const source = this.positionPhotos[index];
        warmImage(source, '160px', 'high');
        image.loading = 'eager'; image.decoding = 'async'; image.fetchPriority = 'high';
        image.sizes = '160px'; image.srcset = source.srcset;
        image.src = source.image;
        image.alt = radios[index].value;
      }
    }
    onChange(event) {
      const input = event.target;
      if (input === this.hatSelect) return this.selectHat();
      const form = input.closest('[data-extra-form]');
      if (!form) return;
      if (input.matches('[data-extra-category]')) this.updateProducts(form);
      if (input.matches('[data-extra-product]')) this.updateVariants(form);
      if (input.matches('[data-extra-variant]')) this.updatePreview(form);
      if (input.matches('[data-extra-position]')) this.updatePosition(form);
    }

    async request(endpoint, payload) {
      const response = await fetch(this.rootURL + endpoint, {
        credentials: 'same-origin', cache: 'no-store', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        ...(payload ? { method: 'POST', body: JSON.stringify(payload) } : {})
      });
      const data = await response.json();
      if (!response.ok || data.errors || data.status) throw new Error(data.description || this.text.error);
      return data;
    }
    sameHat(item, original) {
      const current = item.properties || {}, previous = original.properties || {};
      return item.variant_id === original.variant_id && Object.keys(current).length === Object.keys(previous).length &&
        Object.entries(previous).every(([key, value]) => current[key] === value);
    }
    async rollback(original, group, kind, split, previousExtra) {
      let cart = await this.request('cart.js');
      // Remove only lines belonging to this operation; unrelated cart additions are untouched.
      const extras = cart.items.filter(item => item.properties?._Personalizacion_ID === group &&
        (split || (kind === 'engraving' ? Number(item.variant_id) === Number(this.config.service.id) :
          item.properties?._Complemento === (kind === 'band' ? 'Toquilla adicional' : 'Pluma adicional'))));
      if (!previousExtra && extras.length) cart = await this.request('cart/update.js', { updates: Object.fromEntries(extras.map(item => [item.key, 0])) });
      if (previousExtra) {
        const current = cart.items.find(item => Number(item.variant_id) === Number(previousExtra.variant_id) && item.properties?._Personalizacion_ID === group);
        if (current) cart = await this.request('cart/change.js', { id: current.key, quantity: previousExtra.quantity, properties: previousExtra.properties });
      }
      const hat = cart.items.find(item => this.sameHat(item, original)) || (!split && cart.items.find(item =>
        item.variant_id === original.variant_id && item.properties?._Personalizacion_ID === group && !item.properties?._Servicio && !item.properties?._Complemento));
      if (!hat) throw new Error(this.text.rollback_error);
      const properties = Object.keys(original.properties || {}).length ? original.properties : { _Personalizacion_ID: '' };
      await this.request('cart/change.js', { id: hat.key, quantity: original.quantity, properties });
    }

    async save(form) {
      if (CartItems.changePending || !this.hat || !form.reportValidity()) return;
      const kind = form.dataset.extraForm, shownHat = this.hat, amount = Number(this.quantity.value || 1);
      const initials = form.querySelector('[data-extra-initials]')?.value.trim().toUpperCase();
      const position = form.querySelector('[data-extra-position]:checked')?.value;
      if (kind === 'engraving' && (!/^[A-Z0-9]{1,3}$/.test(initials || '') || !position || !this.config.service.available)) return;
      if (kind !== 'engraving' && (!form.selectedVariant || this.extra(kind))) return;
      const selectedVariant = form.selectedVariant, selectedProduct = form.selectedProduct;
      const group = shownHat.properties?._Personalizacion_ID || `PERS-${crypto.randomUUID()}`;
      let original, previousExtra, split = false, started = false, saved = false;
      const drawer = this.closest('cart-drawer');
      const locked = [...document.querySelectorAll('cart-drawer button, cart-drawer select, cart-drawer input, [name="checkout"]')].map(control => ({ control, disabled: control.disabled }));
      CartItems.changePending = true;
      locked.forEach(({ control }) => { control.disabled = true; });
      this.setAttribute('aria-busy', 'true');
      this.status(this.text.saving);
      let message = this.text.error;
      try {
        const cart = await this.request('cart.js');
        original = cart.items.find(item => item.key === shownHat.key);
        if (!original || original.quantity !== shownHat.quantity || amount < 1 || amount > original.quantity) throw new Error(this.text.changed);
        const linked = cart.items.filter(item => item.properties?._Personalizacion_ID === group && item.key !== original.key);
        previousExtra = linked.find(item => kind === 'engraving' ? Number(item.variant_id) === Number(this.config.service.id) :
          item.properties?._Complemento === (kind === 'band' ? 'Toquilla adicional' : 'Pluma adicional'));
        if (kind !== 'engraving' && previousExtra) throw new Error(this.text.changed);
        split = amount < original.quantity;
        if (split && original.properties?._Personalizacion_ID) throw new Error(this.text.changed);
        const properties = { ...original.properties, _Personalizacion_ID: group };
        const hatName = `${original.product_title} · ${window.GooseOptions?.variant(original.variant_title) || original.variant_title}`;
        const extraProperties = { _Personalizacion_ID: group, _Sombrero: original.product_title, [this.text.hat_property]: hatName };
        if (kind === 'engraving') {
          delete properties.Grabado; delete properties.Engraving;
          delete properties['Posición del grabado']; delete properties['Engraving position'];
          properties[this.text.engraving_property] = initials;
          properties[this.text.position_property] = position;
          Object.assign(extraProperties, { _Servicio: 'Grabado personalizado', _Grabado: initials, [this.text.engraving_property]: initials });
        } else {
          properties[this.text[`${kind}_property`]] = selectedProduct.title;
          properties[this.text[`${kind}_detail_property`]] = selectedVariant.title;
          extraProperties._Complemento = kind === 'band' ? 'Toquilla adicional' : 'Pluma adicional';
        }
        const extraItem = { id: kind === 'engraving' ? Number(this.config.service.id) : selectedVariant.id, quantity: amount, properties: extraProperties };
        const sections = drawer.getSectionsToRender().map(section => section.id);
        started = true;
        let state;
        if (split) {
          await this.request('cart/change.js', { id: original.key, quantity: original.quantity - amount });
          state = await this.request('cart/add.js', { items: [{ id: original.variant_id, quantity: amount, properties }, extraItem], sections, sections_url: location.pathname });
        } else {
          if (previousExtra) await this.request('cart/change.js', { id: previousExtra.key, quantity: amount, properties: { ...previousExtra.properties, ...extraProperties } });
          else await this.request('cart/add.js', { items: [extraItem] });
          // Keys may change when discounts are recalculated after adding an extra.
          const latest = await this.request('cart.js');
          const target = latest.items.find(item => this.sameHat(item, original));
          if (!target) throw new Error(this.text.changed);
          state = await this.request('cart/change.js', { id: target.key, quantity: original.quantity, properties, sections, sections_url: location.pathname });
        }
        saved = true;
        message = this.text.saved;
        try { drawer.renderContents(state, false, '[data-extra-status]'); }
        catch (_) { await drawer.refresh(false, '[data-extra-status]'); }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'goose-cart-extras', cartData: state });
        const updated = drawer.querySelector('goose-cart-extras');
        if (updated) {
          const hat = updated.config.items.find(item => item.properties?._Personalizacion_ID === group && !item.properties?._Servicio && !item.properties?._Complemento);
          if (hat) { updated.hatSelect.value = hat.key; updated.selectHat(); }
        }
      } catch (error) {
        message = saved ? this.text.changed : error.message;
        if (started && !saved && original) {
          try { await this.rollback(original, group, kind, split, previousExtra); }
          catch (_) { message = this.text.rollback_error; }
        }
        try { await drawer.refresh(false, '[data-extra-status]'); } catch (_) {}
      } finally {
        CartItems.changePending = false;
        locked.forEach(({ control, disabled }) => { if (control.isConnected) control.disabled = disabled; });
        if (this.isConnected) this.removeAttribute('aria-busy');
        const updated = drawer.querySelector('goose-cart-extras');
        if (updated) updated.status(message, !saved).focus();
      }
    }
  });
}
